import {
  acceptIntakeFollowup,
  clearIntakeFollowupIfOwned,
  clearIntakeFollowupNotice,
  clearIntakeFollowupSession,
  readIntakeFollowupNotice,
  readIntakeFollowupRecord,
  subscribeIntakeFollowup,
} from "@/ui/intake/lastCareSite";
import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Zeigt den Finder-Hinweis nur, wenn Nutzer und gespeicherte Fall-Ids
 * zu den gerade aufgenommenen Fällen passen.
 * userId null löscht Hinweis und gemerkte Fälle.
 * Ein frisches Formular ohne Übergabe löscht einen liegen gebliebenen Hinweis.
 * Aushängen löscht ihn erst, wenn dieser Screen ihn angenommen hat.
 */
export function useIntakeFollowupNotice(userId: string | null): string | null {
  const seen = useRef(0);
  const [text, setText] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => subscribeIntakeFollowup(() => setTick((n) => n + 1)), []);

  useEffect(() => {
    if (userId == null) {
      clearIntakeFollowupSession();
      seen.current = 0;
      setText(null);
      return;
    }
    const accepted = acceptIntakeFollowup(userId);
    if (accepted) {
      seen.current = accepted.generation;
      setText(accepted.text);
      return;
    }
    const record = readIntakeFollowupRecord();
    if (record && record.userId !== userId) {
      clearIntakeFollowupNotice();
      setText(null);
      return;
    }
    if (record?.handoff && readIntakeFollowupNotice(userId) == null) {
      clearIntakeFollowupNotice();
      setText(null);
      return;
    }
    if (record && !record.handoff && seen.current !== record.generation) {
      clearIntakeFollowupNotice();
      setText(null);
      return;
    }
    setText(readIntakeFollowupNotice(userId));
  }, [tick, userId]);

  const release = useCallback(() => {
    clearIntakeFollowupIfOwned(seen.current);
  }, []);

  useEffect(() => release, [release]);

  useFocusEffect(
    useCallback(() => {
      return () => {
        clearIntakeFollowupIfOwned(seen.current);
      };
    }, []),
  );

  return text;
}
